export function createScreenWakeLock({
  enabled = true,
  navigatorRef = globalThis.navigator,
  documentRef = globalThis.document,
  logger = console
} = {}) {
  const supported = Boolean(
    enabled
    && navigatorRef?.wakeLock
    && typeof navigatorRef.wakeLock.request === 'function'
    && documentRef
  );
  let active = false;
  let destroyed = false;
  let sentinel = null;
  let pending = null;
  let epoch = 0;

  async function release(reason = 'release') {
    epoch += 1;
    const current = sentinel;
    sentinel = null;
    if (!current || current.released || typeof current.release !== 'function') return false;
    try {
      await current.release();
      logger.info?.('[wanli-wake-lock] released', { reason });
      return true;
    } catch (error) {
      logger.warn?.('[wanli-wake-lock] release failed', { reason, error });
      return false;
    }
  }

  async function request(reason = 'request') {
    if (!supported || !active || destroyed || documentRef.visibilityState !== 'visible') return false;
    if (sentinel && !sentinel.released) return true;
    if (pending) return pending;
    const requestEpoch = epoch;
    pending = (async () => {
      try {
        const acquired = await navigatorRef.wakeLock.request('screen');
        if (!active || destroyed || documentRef.visibilityState !== 'visible' || requestEpoch !== epoch) {
          await acquired.release?.();
          return false;
        }
        sentinel = acquired;
        acquired.addEventListener?.('release', () => {
          if (sentinel === acquired) sentinel = null;
          logger.info?.('[wanli-wake-lock] system release', { reason });
        });
        logger.info?.('[wanli-wake-lock] acquired', { reason });
        return true;
      } catch (error) {
        logger.warn?.('[wanli-wake-lock] request failed', { reason, error });
        return false;
      } finally {
        pending = null;
      }
    })();
    return pending;
  }

  function handleVisibilityChange() {
    if (!active) return;
    if (documentRef.visibilityState === 'visible') void request('visibility-visible');
    else void release('visibility-hidden');
  }

  function handleInteraction() {
    if (active && (!sentinel || sentinel.released)) void request('user-interaction');
  }

  function setActive(next, reason = 'screen-state') {
    active = Boolean(next);
    if (active) return request(reason);
    return release(reason);
  }

  async function destroy() {
    destroyed = true;
    active = false;
    documentRef?.removeEventListener?.('visibilitychange', handleVisibilityChange);
    documentRef?.removeEventListener?.('pointerdown', handleInteraction);
    documentRef?.removeEventListener?.('touchstart', handleInteraction);
    documentRef?.removeEventListener?.('keydown', handleInteraction);
    globalThis.removeEventListener?.('pagehide', handlePageHide);
    await release('destroy');
  }

  function handlePageHide() {
    void release('pagehide');
  }

  if (supported) {
    documentRef.addEventListener('visibilitychange', handleVisibilityChange);
    documentRef.addEventListener('pointerdown', handleInteraction, { passive: true });
    documentRef.addEventListener('touchstart', handleInteraction, { passive: true });
    documentRef.addEventListener('keydown', handleInteraction);
    globalThis.addEventListener?.('pagehide', handlePageHide);
  }

  return {
    supported,
    request,
    release,
    setActive,
    destroy,
    state: () => ({ active, acquired: Boolean(sentinel && !sentinel.released), pending: Boolean(pending) })
  };
}
